import { AloftPrice, PROPERTY_GRID_SUBJECT_PROPERTY, orUndefined } from '@aloft/toolkit-shared';
import { Block, Input, ProductColor, theme } from '@aloft/toolkit-sui';

import { useRef, useState } from 'react';

import { setReconciliation } from '../../../../redux/features/reconciliationSlice.ts';
import { selectReconciliation } from '../../../../redux/features/selectors/selectReconciliation.ts';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks.ts';
import { PROPERTY_GRID_ROW_HEIGHT } from '../../consts.ts';
import { usePropertyGridCellFocus } from '../shared/usePropertyGridCellFocus.ts';
import { emptyFieldStyle, popoverInputFocusSx } from '../styles.ts';
import { PropertyGridFieldProps } from '../types.ts';

export const SubjectTotalCostToCureField = ({ colSpan = 1 }: PropertyGridFieldProps) => {
  const dispatch = useAppDispatch();

  const reconciliation = useAppSelector(selectReconciliation);
  const totalCostToCure = orUndefined(AloftPrice.schema.parse)(reconciliation.totalCostToCure);
  const displayValue = totalCostToCure?.toString(true);

  // Local state to track raw input value while typing
  const [rawInputValue, setRawInputValue] = useState<string | undefined>(undefined);
  const [isFocused, setIsFocused] = useState(false);

  const fieldRef = useRef<HTMLInputElement>(null);
  const { isAlertTarget, clearAlertTarget } = usePropertyGridCellFocus({
    cellKeyId: 'totalCostToCure',
    propertyId: PROPERTY_GRID_SUBJECT_PROPERTY,
    fieldRef,
  });

  const handleSetFieldValue = (newValue?: string) => {
    dispatch(
      setReconciliation({
        ...reconciliation,
        totalCostToCure: AloftPrice.fromDollarLike(newValue)?.toJSON(),
      }),
    );
    clearAlertTarget();
  };

  const isEscapePressed = useRef(false);

  const handleFocus = () => {
    isEscapePressed.current = false;
    setIsFocused(true);
    setRawInputValue(totalCostToCure?.toString(true, true));
  };

  const handleBlur = () => {
    setIsFocused(false);

    if (isEscapePressed.current) {
      setRawInputValue(undefined);
      return;
    }

    const trimmedInput = rawInputValue?.trim();

    try {
      if (
        !trimmedInput ||
        trimmedInput === '-$' ||
        trimmedInput === '-' ||
        AloftPrice.fromDollarLike(trimmedInput).equals(AloftPrice.zero)
      ) {
        handleSetFieldValue(undefined);
      } else {
        handleSetFieldValue(trimmedInput);
      }
    } catch {
      handleSetFieldValue(displayValue);
    }

    setRawInputValue(undefined);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    const isEscapeKey = e.key === 'Escape';
    const isEnterKey = e.key === 'Enter' || e.key === 'NumpadEnter';

    if (isEnterKey) {
      e.preventDefault();
      (e.target as HTMLInputElement).blur();
    } else if (isEscapeKey) {
      isEscapePressed.current = true;
      (e.target as HTMLInputElement).blur();
    }
  };

  const { title, backgroundColor } = emptyFieldStyle(!displayValue, 'This field is empty.');

  return (
    <Block gridColumn={`span ${colSpan}`} width="100%">
      <Input
        ref={fieldRef}
        type="text"
        title={isAlertTarget ? 'This field is causing an active alert.' : title}
        value={isFocused ? rawInputValue : displayValue}
        __dangerDoNotUseSx={{
          backgroundColor: isAlertTarget ? theme.colors.alertWarningLight : backgroundColor,
          border: isAlertTarget ? `2px solid ${theme.colors.alertWarning}` : 'none',
          width: '100%',
          height: PROPERTY_GRID_ROW_HEIGHT,
          padding: '4px 8px',
          outline: 'none',
          borderRadius: '0',
          fontSize: '14px',
          textAlign: 'right',
          color: totalCostToCure?.getDisplayColor() as ProductColor | undefined,
          _focusVisible: popoverInputFocusSx,
        }}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onChange={setRawInputValue}
        onClear={() => setRawInputValue(undefined)}
        onKeyDown={handleKeyDown}
      />
    </Block>
  );
};
